import React from "react";
import { Grid, GridColumn, GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { Input } from '@progress/kendo-react-inputs';
import '@progress/kendo-theme-fluent/dist/all.css';
import "./Grids.css";
import { FaPlusCircle, FaWrench, FaFileExport } from "react-icons/fa";
import * as XLSX from "xlsx";
import { Tool } from "../../data/interfaces";

interface ToolsGridProps {
  data: {
    data: Tool[];
    total: number;
  };
  loading: boolean;
  dataState: {
    skip: number;
    take: number;
    filter?: any;
    sort?: any;
  };
  onDataStateChange: (event: GridDataStateChangeEvent) => void;
  onAddToggle: () => void;
  onEditToggle: (toolId: number) => void;
  canEdit?: boolean;
}

const TextFilterCell = (props: any) => {
  const [value, setValue] = React.useState(props.value || '');

  React.useEffect(() => {
    setValue(props.value || '');
  }, [props.value]);

  const handleChange = (e: any) => {
    const newValue = e.value;
    setValue(newValue);
    if (props.onChange) {
      props.onChange({
        value: newValue,
        operator: newValue ? 'contains' : '',
        syntheticEvent: e.syntheticEvent,
      });
    }
  };

  return (
    <div className="k-filtercell">
      <Input value={value} onChange={handleChange} />
    </div>
  );
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-GB");
};

const MakeCell = (props: any) => (
  <td>
    {props.dataItem.make || "-"}
  </td>
);

const EmployeeCell = (props: any) => (
  <td>{props.dataItem.employeeName || "-"}</td>
);

const LastCalibratedCell = (props: any) => (
  <td>{formatDate(props.dataItem.lastCalibrated)}</td>
);

const NextCalibrationCell = (props: any) => {
  const isCalibrationDue = props.dataItem.isCalibrationDue;
  return (
    <td style={{ color: isCalibrationDue ? "#d13438" : "inherit", fontWeight: isCalibrationDue ? "bold" : "normal" }}>
      {formatDate(props.dataItem.nextCalibration)}
      {isCalibrationDue && " (DUE)"}
    </td>
  );
};

const TotalCostsCell = (props: any) => {
  const totalCosts = props.dataItem.costsToDate;
  return (
    <td style={{ textAlign: "right", fontWeight: "500" }}>
      {totalCosts != null ? `£${totalCosts.toFixed(2)}` : '£0.00'}
    </td>
  );
};

const ToolsGrid: React.FC<ToolsGridProps> = (props) => {

  const exportToExcel = () => {
    const exportData = props.data.data.map((tool: Tool) => ({
      Make: tool.make || "",
      Model: tool.model || "",
      "Serial No": tool.serialNo || "",
      "Assigned Employee": tool.employeeName || "",
      "Last Calibrated": formatDate(tool.lastCalibrated),
      "Calibration Due": tool.isCalibrationDue ? "Yes" : "No",
      "Total Costs": tool.costsToDate?.toFixed(2) || "0.00",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tools");
    XLSX.writeFile(wb, "tools.xlsx");
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 80px)',
      padding: '0 20px'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        border: '1px solid #edebe9',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FaWrench style={{ fontSize: '24px', color: '#1E2A3E' }} />
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: '#323130' }}>
            Tool Management
          </h2>
        </div>
        <p style={{ fontSize: '14px', color: '#605e5c', margin: 0 }}>
          Manage and track company tools, calibration dates, and costs.
        </p>
        {props.canEdit === false && (
          <p className='k-form-hint' style={{ margin: 0 }}>You have view-only access to tools.</p>
        )}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {props.canEdit !== false && (
            <Button
              themeColor="primary"
              icon="plus"
              onClick={props.onAddToggle}
            >
              <FaPlusCircle style={{ marginRight: '6px' }} />
              Add Tool
            </Button>
          )}
          <Button onClick={exportToExcel}>
            <FaFileExport style={{ marginRight: '6px' }} />
            Export to Excel
          </Button>
        </div>
      </div>

      {/* Grid */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <Grid
          data={props.data}
          {...props.dataState}
          onDataStateChange={props.onDataStateChange}
          pageable={{ pageSizes: [10, 25, 50, 100] }}
          sortable
          filterable
          onRowDoubleClick={(e) => props.onEditToggle(e.dataItem.toolId)}
          style={{ height: '100%' }}
        >
          <GridColumn
            field="make"
            title="Make"
            width="150px"
            cell={MakeCell}
            filterCell={TextFilterCell}
          />
          <GridColumn
            field="model"
            title="Model"
            width="180px"
            filterCell={TextFilterCell}
          />
          <GridColumn
            field="serialNo"
            title="Serial No"
            width="150px"
            filterCell={TextFilterCell}
          />
          <GridColumn
            field="employeeName"
            title="Assigned Employee"
            width="200px"
            cell={EmployeeCell}
            filterCell={TextFilterCell}
          />
          <GridColumn
            field="lastCalibrated"
            title="Last Calibrated"
            width="160px"
            cell={LastCalibratedCell}
            filter="date"
          />
          <GridColumn
            field="nextCalibration"
            title="Next Calibration"
            width="170px"
            cell={NextCalibrationCell}
            filter="date"
          />
          <GridColumn
            field="costsToDate"
            title="Total Costs"
            width="130px"
            cell={TotalCostsCell}
            filter="numeric"
          />
        </Grid>
      </div>
    </div>
  );
};

export default ToolsGrid;
